import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';
import { PermissionGuard } from './guards/PermissionGuard';

/** The backend key(s) a screen's endpoint requires — passed to `PermissionGuard`. */
export interface PageGuard {
  permission?: string;
  anyOf?: readonly string[];
  allOf?: readonly string[];
}

/** Route `handle` of a `lazyPage` route — its guard, readable without loading the screen. */
export interface PageRouteHandle {
  guard?: PageGuard;
}

/**
 * A route whose screen is downloaded on first visit and, when `guard` is
 * given, rendered behind a `PermissionGuard` with the key(s) the backend
 * enforces for it. The guard is UI reachability only; the backend re-checks
 * every request.
 *
 * The guard is also published as the route's `handle`, so the navigation
 * tests can prove every sidebar link opens its page without downloading it.
 */
export function lazyPage(path: string, load: () => Promise<ComponentType>, guard?: PageGuard): RouteObject {
  return {
    path,
    handle: { guard } satisfies PageRouteHandle,
    lazy: async () => {
      const Page = await load();
      if (!guard) return { Component: Page };
      function GuardedPage() {
        return (
          <PermissionGuard {...guard}>
            <Page />
          </PermissionGuard>
        );
      }
      return { Component: GuardedPage };
    },
  };
}
